import { apiUrl } from "../components/ApiManager/AxiosHeaders";

export function getInvoiceFollowupErrorMessage(error, responseData) {
  if (responseData?.error) return String(responseData.error);
  if (responseData?.message && responseData?.status === false) {
    return String(responseData.message);
  }
  if (error?.response?.data?.error) return String(error.response.data.error);
  if (error?.response?.data?.message) return String(error.response.data.message);
  if (error?.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function createInvoiceFollowup(payload) {
  const response = await apiUrl.post("/invoice-followups", payload);
  return response.data;
}

export async function updateInvoiceFollowup(id, payload) {
  const response = await apiUrl.put(`/invoice-followups/${id}`, payload);
  return response.data;
}

export async function reassignInvoiceFollowup(followupId, assignedId) {
  const response = await apiUrl.post("/invoice-followups/reassign", {
    followupId,
    assignedId,
  });
  return response.data;
}

export async function fetchInvoiceFollowupsByInvoice(financeInvoiceId) {
  const response = await apiUrl.get(
    `/invoice-followups/by-invoice/${financeInvoiceId}`,
  );
  return response.data?.data || [];
}

// Must not exceed BY_INVOICES_MAX_IDS in InvoiceFollowupController.
const BY_INVOICES_CHUNK_SIZE = 200;

/**
 * Follow-ups for many invoices, keyed by invoice id.
 * Ids whose chunk request failed are absent from the result.
 */
export async function fetchInvoiceFollowupsByInvoices(financeInvoiceIds = []) {
  const ids = uniqueInvoiceIds(financeInvoiceIds);
  const rowsByInvoice = {};
  for (let i = 0; i < ids.length; i += BY_INVOICES_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + BY_INVOICES_CHUNK_SIZE);
    try {
      const response = await apiUrl.get("/invoice-followups/by-invoices", {
        params: { invoice_ids: chunk.join(",") },
      });
      const data = response.data?.status ? response.data?.data : null;
      if (data) {
        chunk.forEach((id) => {
          const rows = data[id] ?? data[String(id)] ?? data[Number(id)];
          if (Array.isArray(rows)) rowsByInvoice[id] = rows;
        });
      }
    } catch {
      // Leave this chunk's ids out so callers treat them as unavailable.
    }
  }
  return rowsByInvoice;
}

/**
 * Bell/hover summaries for invoice list pages.
 * Same shape as summarizeInvoiceFollowupsForList: { total, pending, completed }.
 */
export async function fetchInvoiceFollowupSummariesByInvoices(
  financeInvoiceIds = [],
) {
  const ids = uniqueInvoiceIds(financeInvoiceIds);
  const summaries = {};
  for (let i = 0; i < ids.length; i += BY_INVOICES_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + BY_INVOICES_CHUNK_SIZE);
    try {
      const response = await apiUrl.get("/invoice-followups/by-invoices", {
        params: { invoice_ids: chunk.join(","), summary: 1 },
      });
      const data = response.data?.status ? response.data?.data : null;
      if (data) {
        chunk.forEach((id) => {
          const summary = data[id] ?? data[String(id)] ?? data[Number(id)];
          if (summary && typeof summary === "object" && !Array.isArray(summary)) {
            summaries[id] = summary;
          }
        });
      }
    } catch {
      // Leave this chunk's ids out so callers treat them as unavailable.
    }
  }
  return summaries;
}

function uniqueInvoiceIds(financeInvoiceIds = []) {
  return [
    ...new Set(
      financeInvoiceIds
        .filter((id) => id != null && String(id).trim())
        .map((id) => String(id)),
    ),
  ];
}

export async function fetchAllInvoiceFollowupCounts() {
  const response = await apiUrl.get("/getAllInvoiceFollowups");
  return response.data?.data || {};
}
